import { useCallback, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { debounce, uniqBy } from "lodash";
import { ApiConstant, AppConstant, LangConstant } from "const";
import { ApiUtils, HumpUtils, SystemUtils } from "utils";
import { formatArray2Key, formatPagingParams } from "utils/common.utils";

const DEFAULT_FILTERS = {
  isHiddenOaAccount: true,
  keyword: "",
  page: 1,
};

export default function useAccountList({ groupId } = {}) {
  const { t: getLabel } = useTranslation();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [accountList, setAccountList] = useState({ data: [], isEnd: false });

  const query = useQuery({
    enabled: Boolean(groupId),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: [ApiConstant.REACT_QUERY_KEYS.getAccountList, groupId, filters],
    queryFn: async () => {
      const { page, keyword, ...otherConditions } = filters;
      const offset = (filters.page - 1) * AppConstant.PAGE_SIZE;

      const params = HumpUtils.decamelizeKeys(
        formatPagingParams(
          formatArray2Key({
            paging: 1,
            sinceTime: 0,
            limit: AppConstant.PAGE_SIZE,
            offset,
            search: filters.keyword || undefined,
            ignoreBot: 1,
            ignoreGroupIds: [groupId],
            ...otherConditions,
          }),
        ),
      );

      if (!filters.keyword) params.filter = JSON.stringify({ state: 1 });

      const response = await ApiUtils.createApiWithToken(false).get(ApiConstant.GET_ACCOUNT_LIST, params);
      return HumpUtils.camelizeKeys(response.data);
    },
    onSuccess: response => {
      if (response?.status === ApiConstant.STT_OK) {
        const incoming = response.data?.data ?? [];
        setAccountList(prev => ({
          data: filters.page === 1 ? incoming : uniqBy([...prev.data, ...incoming], "id"),
          isEnd: incoming.length < AppConstant.PAGE_SIZE,
        }));
      }
    },
    onError: () => {
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
    },
  });

  // Debounced search — resets to page 1
  const handleSearch = useCallback(
    debounce(value => {
      setFilters({ ...DEFAULT_FILTERS, keyword: value, page: 1 });
    }, AppConstant.INPUT_DEBOUNCE_TIME),
    [],
  );

  // Append next page when scrolled to bottom
  const handleLoadMore = () => {
    if (!accountList.isEnd && !query.isFetching) {
      setFilters(prev => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const handleResetQuery = () => {
    setAccountList({ data: [], isEnd: false });

    if (filters.page === 1 && filters.keyword === "") {
      query.refetch();
    } else {
      setFilters(DEFAULT_FILTERS);
    }
  };

  return {
    accounts: accountList.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    handleSearch,
    handleLoadMore,
    handleResetQuery,
  };
}
